import { useCallback, useEffect, useMemo, useState } from 'react'
import { DatePicker, Form, Input, Modal, Select, Space, Typography, message } from 'antd'
import dayjs from 'dayjs'
import { tasksApi, type LeadOption, type ProfileOption, type TaskItem, type TaskPayload } from '../../services/tasksApi'
import { operatorsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { DEV_ADMIN, roleRank, seesOthersTasks } from '../../constants/roles'

// Общее для раздела «Задачи» и вкладки «Задачи» в карточке лида: значки,
// подписи, окно задачи, окно закрытия с комментарием и действия. Одно на оба
// места - чтобы задача везде создавалась, закрывалась и правилась одинаково.

const { Text } = Typography

export function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

// Объёмные значки действий в списке задач. Нарисованы вручную, а не взяты из
// набора antd: там иконки одноцветные и плоские, а по каждому действию должно
// быть понятно с одного взгляда - зелёная галочка завершает, серый пузырь
// уводит в переписку, синий карандаш открывает правку.
export const DoneIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden>
        <defs>
            <linearGradient id="taskDoneGrad" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                <stop stopColor="#4ade80" />
                <stop offset="1" stopColor="#059669" />
            </linearGradient>
        </defs>
        {/* Тень под штрихом даёт тот самый объём: светлая грань сверху, тёмная снизу. */}
        <path d="M4.6 12.9 9.4 17.7 19.4 6.6" stroke="#052e1b" strokeWidth="4.6"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.55" transform="translate(0.5 1)" />
        <path d="M4.6 12.9 9.4 17.7 19.4 6.6" stroke="url(#taskDoneGrad)" strokeWidth="3.6"
            strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.6 11.9 9.2 15.6" stroke="#bbf7d0" strokeWidth="1.1" strokeLinecap="round" opacity="0.75" />
    </svg>
)

export const ChatIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden>
        <defs>
            <linearGradient id="taskChatGrad" x1="4" y1="3" x2="20" y2="21" gradientUnits="userSpaceOnUse">
                <stop stopColor="#ffffff" />
                <stop offset="1" stopColor="#94a3b8" />
            </linearGradient>
        </defs>
        <path d="M6.9 17.4 5.1 20.6c-.25.45.14.97.63.83l3.62-1.03" fill="url(#taskChatGrad)" />
        <circle cx="12" cy="11.4" r="8.1" fill="#111418" stroke="url(#taskChatGrad)" strokeWidth="2.1" />
        <circle cx="8.4" cy="11.4" r="1.35" fill="#e2e8f0" />
        <circle cx="12" cy="11.4" r="1.35" fill="#e2e8f0" />
        <circle cx="15.6" cy="11.4" r="1.35" fill="#e2e8f0" />
    </svg>
)

export const EditIcon = () => (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden>
        <defs>
            <linearGradient id="taskEditGrad" x1="6" y1="3" x2="20" y2="17" gradientUnits="userSpaceOnUse">
                <stop stopColor="#60a5fa" />
                <stop offset="1" stopColor="#1d4ed8" />
            </linearGradient>
        </defs>
        {/* Карандаш контуром - как на образце, а не сплошной заливкой. */}
        <path d="M15.2 3.9 19.4 8.1 9.6 17.9 4.7 19.1 5.9 14.2Z"
            stroke="url(#taskEditGrad)" strokeWidth="2" strokeLinejoin="round" fill="none" />
        <path d="M13.5 5.6 17.7 9.8" stroke="url(#taskEditGrad)" strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
        <rect x="4.4" y="20.4" width="15.2" height="2.4" rx="1.2" fill="url(#taskEditGrad)" />
    </svg>
)

export const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    pending: { label: 'Ожидает', color: 'default' },
    in_progress: { label: 'В работе', color: 'processing' },
    completed: { label: 'Выполнена', color: 'success' },
    cancelled: { label: 'Отменена', color: 'default' },
}

export const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
    low: { label: 'Низкий', color: 'default' },
    medium: { label: 'Обычный', color: 'blue' },
    high: { label: 'Высокий', color: 'orange' },
    urgent: { label: 'Срочно', color: 'red' },
}

export const CLOSED = ['completed', 'cancelled']
export const DATE_FORMAT = 'DD.MM.YYYY HH:mm'

type AssigneeGroup = { label: string; options: { value: string; label: string }[] }

// Добавляет текущего исполнителя в список, если его там нет.
function withCurrent(groups: AssigneeGroup[], current: { value: string; label: string } | null): AssigneeGroup[] {
    if (!current || groups.some((group) => group.options.some((option) => option.value === current.value))) return groups
    return [{ label: 'Сейчас', options: [current] }, ...groups]
}

// Исполнитель задачи для окна: сотрудник или команда, если сотрудника нет.
export function assigneeValue(task: TaskItem): string | undefined {
    if (task.assigned_to_id) return `op:${task.assigned_to_id}`
    if (task.visibility_profile_id) return `pr:${task.visibility_profile_id}`
    return undefined
}

export type TaskOperator = { id: number; username: string; full_name?: string | null; role?: string }

// Лид, на который ставится задача из его карточки: выбирать лида не нужно.
export type FixedLead = { id: number; label: string; geo?: string | null; crm_ww_id?: string | null }

/**
 * Окно задачи, окно закрытия с комментарием и действия над задачей.
 * onChanged вызывается после каждого успешного действия - список
 * перечитывается. fixedLead - задача из карточки лида: лид подставлен и
 * не меняется.
 */
export function useTaskEditor({ onChanged, fixedLead }: { onChanged: () => void | Promise<void>; fixedLead?: FixedLead }) {
    const { user } = useAuthStore()
    const isManager = seesOthersTasks(user?.role)
    const isDev = user?.role === DEV_ADMIN

    const [operators, setOperators] = useState<TaskOperator[]>([])
    useEffect(() => {
        // Список сотрудников нужен только там, где можно назначить исполнителя.
        if (!isManager) return
        operatorsApi.getAll()
            .then((response) => setOperators(response.data))
            .catch(() => undefined)
    }, [isManager])

    // Команды, на которые можно поставить задачу: свои, руководителям - все.
    const [profileOptions, setProfileOptions] = useState<ProfileOption[]>([])
    useEffect(() => {
        tasksApi.getProfileOptions()
            .then((response) => setProfileOptions(response.data))
            .catch(() => undefined)
    }, [])

    // Кому можно ставить задачи и чьи задачи можно выбрать в фильтре: себе и
    // тем, кто младше по рангу. Сервер проверяет то же самое.
    const assignableOperators = useMemo(() => operators.filter((operator) =>
        isDev || operator.id === user?.id || roleRank(operator.role) < roleRank(user?.role)
    ), [operators, isDev, user])

    // Закрытие задачи: спрашиваем, чем закрыли - у задачи из CRM текст уйдёт
    // в их поле «Результат».
    const [closing, setClosing] = useState<TaskItem | null>(null)
    const [closeComment, setCloseComment] = useState('')
    const [closingSaving, setClosingSaving] = useState(false)

    // Исполнитель в окне задачи - сотрудник («op:ID») или команда («pr:ID»).
    const [originalAssignee, setOriginalAssignee] = useState<string | undefined>(undefined)
    // Текущий исполнитель задачи, которого может не быть в списке выбора
    // (например, коллега, взявший задачу команды).
    const [currentAssignee, setCurrentAssignee] = useState<{ value: string; label: string } | null>(null)
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<TaskItem | null>(null)
    const [saving, setSaving] = useState(false)
    const [form] = Form.useForm()

    // Выбор лида с поиском: список приходит с сервера, фильтровать локально
    // нечего - лидов может быть намного больше, чем помещается в одну выдачу.
    const [leadOptions, setLeadOptions] = useState<LeadOption[]>([])
    const [leadsLoading, setLeadsLoading] = useState(false)

    const searchLeads = useCallback(async (value?: string) => {
        if (fixedLead) return
        setLeadsLoading(true)
        try {
            const { data } = await tasksApi.getLeadOptions(value)
            setLeadOptions(data)
        } catch {
            setLeadOptions([])
        } finally {
            setLeadsLoading(false)
        }
    }, [fixedLead])

    const fixedOption = (lead: FixedLead): LeadOption => ({
        id: lead.id, label: lead.label, geo: lead.geo ?? null, crm_ww_id: lead.crm_ww_id ?? null,
    })

    const openCreate = () => {
        setEditing(null)
        form.resetFields()
        setOriginalAssignee(undefined)
        setCurrentAssignee(null)
        form.setFieldsValue({
            priority: 'medium', status: 'pending', assignee: user?.id ? `op:${user.id}` : undefined,
            ...(fixedLead ? { lead_id: fixedLead.id } : {}),
        })
        if (fixedLead) setLeadOptions([fixedOption(fixedLead)])
        else void searchLeads()
        setFormOpen(true)
    }

    const openEdit = (task: TaskItem) => {
        setEditing(task)
        form.setFieldsValue({
            title: task.title,
            description: task.description || '',
            priority: task.priority,
            status: task.status,
            assignee: assigneeValue(task),
            deadline: task.deadline ? dayjs(task.deadline) : null,
            lead_id: task.lead_id ?? undefined,
        })
        setOriginalAssignee(assigneeValue(task))
        const value = assigneeValue(task)
        setCurrentAssignee(value ? {
            value,
            label: task.assigned_to_id
                ? (task.assigned_to_name || `Сотрудник #${task.assigned_to_id}`)
                : `Команда «${task.visibility_profile_name || task.visibility_profile_id}»`,
        } : null)
        // Привязанный лид должен быть в списке, иначе поле покажет пустоту.
        setLeadOptions(task.lead_id
            ? [{ id: task.lead_id, label: task.lead_title || `Лид #${task.lead_id}`, geo: task.lead_geo, crm_ww_id: task.lead_crm_ww_id }]
            : [])
        void searchLeads()
        setFormOpen(true)
    }

    const submit = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const payload: TaskPayload = {
                title: values.title.trim(),
                description: (values.description || '').trim() || null,
                priority: values.priority,
                status: values.status,
                deadline: values.deadline.toISOString(),
                lead_id: values.lead_id ?? null,
            }
            // Исполнитель меняется, только если его поменяли в окне: иначе
            // правка задачи команды, которую взял сотрудник, сняла бы команду.
            if (!editing || values.assignee !== originalAssignee) {
                const [kind, rawId] = String(values.assignee || '').split(':')
                const id = Number(rawId) || null
                payload.assigned_to_id = kind === 'op' ? id : null
                payload.visibility_profile_id = kind === 'pr' ? id : null
            }
            const { data } = editing
                ? await tasksApi.updateTask(editing.id, payload)
                : await tasksApi.createTask(payload)
            message.success(editing ? 'Задача обновлена' : 'Задача создана')
            if (data?.crm_warning) message.warning(data.crm_warning)
            setFormOpen(false)
            await onChanged()
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) return
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const setStatus = async (task: TaskItem, status: string, comment?: string) => {
        try {
            const { data } = await tasksApi.updateTask(task.id, {
                status,
                ...(comment !== undefined ? { close_comment: comment || null } : {}),
            })
            // У нас задача уже закрыта - о неудаче передачи в CRM сообщаем
            // отдельно, само действие не отменяется.
            if (data?.crm_warning) message.warning(data.crm_warning)
            await onChanged()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const requestClose = (task: TaskItem) => {
        // Комментарий спрашиваем при закрытии любой задачи: он сохраняется в
        // самой задаче, а у связанных с CRM ещё и уходит в их «Результат».
        setClosing(task)
        setCloseComment('')
    }

    const submitClose = async () => {
        if (!closing) return
        setClosingSaving(true)
        await setStatus(closing, 'completed', closeComment.trim())
        setClosingSaving(false)
        setClosing(null)
    }

    // «Взять себе» задачу команды: исполнителем становится нажавший.
    const take = async (task: TaskItem) => {
        try {
            await tasksApi.takeTask(task.id)
            message.success('Задача ваша')
            await onChanged()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const remove = async (task: TaskItem) => {
        try {
            await tasksApi.deleteTask(task.id)
            message.success('Задача удалена')
            await onChanged()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const modals = (
        <>
            <Modal
                title={closing ? `Закрыть задачу #${closing.id}` : 'Закрыть задачу'}
                open={Boolean(closing)}
                onCancel={() => setClosing(null)}
                onOk={submitClose}
                okText="Закрыть задачу"
                cancelText="Отмена"
                confirmLoading={closingSaving}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Input.TextArea
                        rows={3}
                        maxLength={2000}
                        autoFocus
                        value={closeComment}
                        onChange={(event) => setCloseComment(event.target.value)}
                        placeholder="Что сделано — необязательно"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {closing?.crm_task_id
                            ? `Комментарий сохранится в задаче и уйдёт в поле «Результат» задачи CRM #${closing.crm_task_id} — его увидит их сотрудник. `
                            : 'Комментарий сохранится в задаче и будет виден во вкладке «Закрытые задачи». '}
                        Оставите пустым — запишется, кто и когда закрыл задачу.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title={editing ? `Задача #${editing.id}` : 'Новая задача'}
                open={formOpen}
                onCancel={() => setFormOpen(false)}
                onOk={submit}
                okText={editing ? 'Сохранить' : 'Создать'}
                cancelText="Отмена"
                confirmLoading={saving}
                width={560}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Что сделать" rules={[{ required: true, message: 'Заполните поле' }]}>
                        <Input placeholder="Например: проверь чат с агентом и актуализируй статус" />
                    </Form.Item>
                    <Form.Item name="description" label="Подробности">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Space size={12} style={{ width: '100%' }} wrap>
                        <Form.Item name="priority" label="Приоритет" style={{ minWidth: 160 }}>
                            <Select
                                options={Object.entries(PRIORITY_LABELS).map(([value, item]) => ({
                                    value, label: item.label,
                                }))}
                            />
                        </Form.Item>
                        <Form.Item name="status" label="Статус" style={{ minWidth: 160 }}>
                            <Select
                                options={Object.entries(STATUS_LABELS).map(([value, item]) => ({
                                    value, label: item.label,
                                }))}
                            />
                        </Form.Item>
                        {/* Срок обязателен: задача ставится на конкретное время и
                            после него считается просроченной. */}
                        <Form.Item
                            name="deadline"
                            label="Срок: дата и время"
                            style={{ minWidth: 220 }}
                            rules={[{ required: true, message: 'Укажите дату и время' }]}
                        >
                            <DatePicker showTime={{ format: 'HH:mm' }} format={DATE_FORMAT} style={{ width: '100%' }} />
                        </Form.Item>
                    </Space>
                    <Form.Item
                        name="lead_id"
                        label="Лид"
                        extra={fixedLead ? 'Задача по этому лиду' : 'Необязательно: задача привяжется к карточке и её чату'}
                    >
                        <Select
                            allowClear={!fixedLead}
                            disabled={Boolean(fixedLead)}
                            showSearch
                            placeholder="Начните вводить название или ID CRM WW"
                            // Фильтрует сервер: он же решает, чьи лиды показывать.
                            filterOption={false}
                            loading={leadsLoading}
                            onSearch={(value) => void searchLeads(value)}
                            onFocus={() => { if (!leadOptions.length) void searchLeads() }}
                            notFoundContent={leadsLoading ? 'Ищем…' : 'Ничего не найдено'}
                            options={leadOptions.map((lead) => ({
                                value: lead.id,
                                label: [lead.label, lead.geo, lead.crm_ww_id ? `CRM ${lead.crm_ww_id}` : null]
                                    .filter(Boolean).join(' · '),
                            }))}
                        />
                    </Form.Item>

                    {/* Исполнитель - сотрудник или команда. Оператор ставит задачи
                        себе и своей команде, руководитель - и младшим сотрудникам. */}
                    <Form.Item
                        name="assignee"
                        label="Исполнитель"
                        extra="Задачу команды видят все её сотрудники, любой может взять её себе"
                        rules={[{ required: true, message: 'Выберите исполнителя' }]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            placeholder="Сотрудник или команда"
                            options={withCurrent([
                                {
                                    label: 'Сотрудники',
                                    options: (isManager ? assignableOperators : (user ? [{ id: user.id, username: user.username, full_name: user.full_name }] : []))
                                        .map((operator) => ({ value: `op:${operator.id}`, label: operator.full_name || operator.username })),
                                },
                                ...(profileOptions.length ? [{
                                    label: 'Команды',
                                    options: profileOptions.map((profile) => ({ value: `pr:${profile.id}`, label: `Команда «${profile.name}»` })),
                                }] : []),
                            ], currentAssignee)}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )

    return { isManager, assignableOperators, openCreate, openEdit, requestClose, setStatus, remove, take, modals }
}
